import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlusCircle } from "react-feather";
import { useDispatch, useSelector } from "react-redux";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronUp, RotateCcw } from "feather-icons-react/build/IconComponents";
import { setToogleHeader } from "../../../core/redux/action";
import SettingsSideBar from "../settingssidebar";
import {
  createSMSTemplate,
  getSMSTemplates,
  deleteSMSTemplate,
  updateSMSTemplate,
} from "../../../service/ApiServices";
import Swal from "sweetalert2";
import Table from "../../../core/pagination/datatable";
import axios from "axios";

const SmsTemplates = () => {

  const dispatch = useDispatch();
  const data = useSelector((state) => state.toggle_header);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");
  const [templates, setTemplates] = useState([]);
  const [editTemplate, setEditTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [placeholders, setPlaceholders] = useState([]);
  const [newPlaceholder, setNewPlaceholder] = useState({ name: "" });
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchPlaceholders();
  }, []);

  const fetchPlaceholders = async () => {
    try {
      const response = await axios.get("/placeholders");
      if (
        response.data &&
        response.data.data &&
        response.data.data.placeholders
      ) {
        setPlaceholders(response.data.data.placeholders);
      } else {
        setPlaceholders([]);
      }
    } catch (error) {
      console.error("Error fetching placeholders:", error);
      setPlaceholders([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch placeholders",
      });
    }
  };

  const handleAddPlaceholder = async () => {
    try {
      if (!newPlaceholder.name.trim()) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Please enter a placeholder name",
        });
        return;
      }

      const response = await axios.post("/placeholders", {
        name: newPlaceholder.name.trim().toUpperCase(),
      });

      if (response.data && response.data.status === "success") {
        setNewPlaceholder({ name: "" });
        fetchPlaceholders();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Placeholder added successfully",
        });
      }
    } catch (error) {
      console.error("Error adding placeholder:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to add placeholder",
      });
    }
  };

  const handleDeletePlaceholder = async (id) => {
    try {
      await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await axios.delete(`/placeholders/${id}`);
          fetchPlaceholders();
          Swal.fire("Deleted!", "Placeholder has been deleted.", "success");
        }
      });
    } catch (error) {
      console.error("Error deleting placeholder:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to delete placeholder",
      });
    }
  };

  const insertPlaceholder = (placeholder) => {
    const cursorPosition = document.getElementById(
      "templateDescription"
    ).selectionStart;
    const textBefore = templateDescription.substring(0, cursorPosition);
    const textAfter = templateDescription.substring(cursorPosition);
    const newText = textBefore + `{{${placeholder.name}}}` + textAfter;
    setTemplateDescription(newText);
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await getSMSTemplates();
      if (response.data && response.data.SMSTemplates) {
        const transformedData = response.data.SMSTemplates.map((template) => ({
          key: template._id,
          templateName: template.templateName,
          templateDescription: template.templateDescription,
          placeholders: template.placeholders,
        }));
        setTemplates(transformedData);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch templates",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to extract placeholders from template description
  const extractPlaceholders = (text) => {
    const regex = /{{([^}]+)}}/g;
    const matches = text.match(regex) || [];
    return matches.map((match) => match.replace(/[{}]/g, ""));
  };

  // Function to find placeholder IDs from names
  const getPlaceholderIds = (placeholderNames) => {
    return placeholders
      .filter((p) => placeholderNames.includes(p.name))
      .map((p) => p._id);
  };

  const handleSMSTemplate = async (e) => {
    e.preventDefault();
    try {
      // Extract used placeholders from template description
      const usedPlaceholderNames = extractPlaceholders(templateDescription);
      const placeholderIds = getPlaceholderIds(usedPlaceholderNames);

      const response = await createSMSTemplate({
        templateName,
        templateDescription,
        placeholders: placeholderIds, // Add placeholders array
      });

      if (response.data && response.data.status === "success") {
        setTemplateName("");
        setTemplateDescription("");
        fetchTemplates();
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "SMS Template created successfully.",
        });
      }
    } catch (error) {
      console.error("Error creating template:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to create template",
      });
    }
  };

  const handleEdit = async (template) => {
    setEditTemplate(template);
    setTemplateName(template.templateName);
    setTemplateDescription(template.templateDescription);
  };

  const handleUpdate = async () => {
    try {
      // Extract used placeholders from template description
      const usedPlaceholderNames = extractPlaceholders(templateDescription);
      const placeholderIds = getPlaceholderIds(usedPlaceholderNames);

      await updateSMSTemplate(editTemplate.key, {
        templateName,
        templateDescription,
        placeholders: placeholderIds, // Add placeholders array
      });

      Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Template updated successfully",
      });

      setEditTemplate(null);
      setTemplateName("");
      setTemplateDescription("");
      fetchTemplates();
    } catch (error) {
      console.error("Error updating template:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to update template",
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          await deleteSMSTemplate(id);
          fetchTemplates();
          Swal.fire("Deleted!", "Template has been deleted.", "success");
        }
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete template",
      });
    }
  };

  const columns = [
    {
      title: "Template Name",
      dataIndex: "templateName",
      width: 700,
    },
    {
      title: "Actions",
      dataIndex: "actions",
      render: (_, record) => (
        <div className="edit-delete-action">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => {
              Swal.fire({
                title: "Template Details",
                html: `
                  <div style="text-align: left">
                    <p><strong>Name:</strong> ${record.templateName}</p>
                    <p><strong>Description:</strong> ${record.templateDescription}</p>
                  </div>
                `,
                confirmButtonText: "Close",
              });
            }}
          >
            <i className="feather-eye"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            data-bs-toggle="modal"
            data-bs-target="#edit-template"
            onClick={() => handleEdit(record)}
          >
            <i className="feather-edit"></i>
          </Link>
          <Link className="p-2" to="#" onClick={() => handleDelete(record.key)}>
            <i className="feather-trash-2"></i>
          </Link>
        </div>
      ),
    },
  ];

  const renderRefreshTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Refresh
    </Tooltip>
  );

  const renderCollapseTooltip = (props) => (
    <Tooltip id="refresh-tooltip" {...props}>
      Collapse
    </Tooltip>
  );

  const renderPlaceholders = () => {
    if (!Array.isArray(placeholders)) {
      return null;
    }

    return placeholders.map((placeholder) => (
      <button
        key={placeholder._id}
        type="button"
        className="btn btn-outline-primary me-2 mb-2"
        onClick={() => insertPlaceholder(placeholder)}
        title={placeholder.description}
      >
        {placeholder.name}
      </button>
    ));
  };

  return (
    <div>
      <div className="page-wrapper">
        <div className="content settings-content">
          <div className="page-header settings-pg-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Settings</h4>
                <h6>Manage your settings on portal</h6>
              </div>
            </div>
            <ul className="table-top-head">
              <li>
                <OverlayTrigger placement="top" overlay={renderRefreshTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    onClick={fetchTemplates}
                  >
                    <RotateCcw />
                  </Link>
                </OverlayTrigger>
              </li>
              <li>
                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                  <Link
                    data-bs-toggle="tooltip"
                    data-bs-placement="top"
                    id="collapse-header"
                    className={data ? "active" : ""}
                    onClick={() => {
                      dispatch(setToogleHeader(!data));
                    }}
                  >
                    <ChevronUp />
                  </Link>
                </OverlayTrigger>
              </li>
            </ul>
          </div>
          <div className="row">
            <div className="col-xl-12">
              <div className="settings-wrapper d-flex">
                <SettingsSideBar />
                <div className="settings-page-wrap">
                  <div className="setting-title">
                    <h4>SMS Templates</h4>
                  </div>
                  <div className="card">
                    <div className="card-body">
                      <div className="d-flex justify-content-between mb-3">
                        <button
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#add-template"
                        >
                          <PlusCircle className="me-2" />
                          Add New SMS Template
                        </button>
                        <button
                          className="btn btn-secondary"
                          onClick={() => setShowPlaceholderModal(true)}
                        >
                          <PlusCircle className="me-2" />
                          Add New Placeholder
                        </button>
                      </div>

                      <div className="table-responsive">
                        <Table
                          columns={columns}
                          dataSource={templates}
                          loading={loading}
                        />
                      </div>

                      <div className="modal fade" id="add-template" data-bs-backdrop="static" data-bs-keyboard="false">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Add SMS Template</h5>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Name
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={templateName}
                                    onChange={(e) =>
                                      setTemplateName(e.target.value)
                                    }
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Description
                                  </label>
                                  <div className="position-relative">
                                    <textarea
                                      id="templateDescription"
                                      className="form-control"
                                      rows="5"
                                      value={templateDescription}
                                      onChange={(e) =>
                                        setTemplateDescription(e.target.value)
                                      }
                                    ></textarea>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Available Placeholders
                                  </label>
                                  <div className="placeholder-buttons">
                                    {renderPlaceholders()}
                                  </div>
                                </div>
                              </form>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                              >
                                Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSMSTemplate}
                                data-bs-dismiss="modal"
                              >
                                Save Template
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="modal fade" id="edit-template" data-bs-backdrop="static" data-bs-keyboard="false">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">Edit SMS Template</h5>
                              <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Name
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={templateName}
                                    onChange={(e) =>
                                      setTemplateName(e.target.value)
                                    }
                                  />
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Template Description
                                  </label>
                                  <div className="position-relative">
                                    <textarea
                                      id="editTemplateDescription"
                                      className="form-control"
                                      rows="5"
                                      value={templateDescription}
                                      onChange={(e) =>
                                        setTemplateDescription(e.target.value)
                                      }
                                    ></textarea>
                                  </div>
                                </div>
                                <div className="mb-3">
                                  <label className="form-label">
                                    Available Placeholders
                                  </label>
                                  <div className="placeholder-buttons">
                                    {renderPlaceholders()}
                                  </div>
                                </div>
                              </form>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                              >
                                Close
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleUpdate}
                                data-bs-dismiss="modal"
                              >
                                Update Template
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        className={`modal fade ${
                          showPlaceholderModal ? "show" : ""
                        }`}
                        id="add-placeholder"
                        style={{
                          display: showPlaceholderModal ? "block" : "none",
                        }}
                      >
                        <div className="modal-dialog modal-dialog-centered">
                          <div className="modal-content">
                            <div className="modal-header">
                              <h5 className="modal-title">
                                Manage Placeholders
                              </h5>
                              <button
                                type="button"
                                className="btn-close"
                                onClick={() => setShowPlaceholderModal(false)}
                              ></button>
                            </div>
                            <div className="modal-body">
                              <form className="mb-4">
                                <div className="input-group">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={newPlaceholder.name}
                                    onChange={(e) =>
                                      setNewPlaceholder({
                                        name: e.target.value,
                                      })
                                    }
                                    placeholder="Enter placeholder name (e.g., CUSTOMER_NAME)"
                                  />
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={handleAddPlaceholder}
                                  >
                                    Add
                                  </button>
                                </div>
                              </form>

                              <div className="existing-placeholders">
                                <h6 className="mb-3">Existing Placeholders</h6>
                                {Array.isArray(placeholders) &&
                                placeholders.length > 0 ? (
                                  <div className="list-group">
                                    {placeholders.map((placeholder) => (
                                      <div
                                        key={placeholder._id}
                                        className="list-group-item d-flex justify-content-between align-items-center"
                                      >
                                        <span className="placeholder-name">
                                          {placeholder.name}
                                        </span>
                                        <button
                                          className="btn btn-sm btn-danger"
                                          onClick={() =>
                                            handleDeletePlaceholder(
                                              placeholder._id
                                            )
                                          }
                                        >
                                          <i className="feather-trash-2"></i>
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-muted">
                                    No placeholders added yet
                                  </p>
                                )}
                              </div>
                            </div>
                            <div className="modal-footer">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => setShowPlaceholderModal(false)}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsTemplates;
